import React, { useState } from 'react';

const XOGame = () => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [xIsNext, setXIsNext] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [winningLine, setWinningLine] = useState(null);

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], // top row
      [3, 4, 5], // middle row
      [6, 7, 8], // bottom row
      [0, 3, 6], // left col
      [1, 4, 7], // middle col
      [2, 5, 8], // right col
      [0, 4, 8], // main diag
      [2, 4, 6], // anti diag
    ];

    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line: lines[i] };
      }
    }
    return null;
  };

  const handleClick = (index) => {
    if (board[index] || gameOver) return;

    const newBoard = board.slice();
    newBoard[index] = xIsNext ? 'X' : 'O';
    setBoard(newBoard);
    setXIsNext(!xIsNext);

    const result = calculateWinner(newBoard);
    if (result) {
      setGameOver(true);
      setWinningLine(result.line);
    } else if (newBoard.every((square) => square !== null)) {
      setGameOver(true);
    }
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setXIsNext(true);
    setGameOver(false);
    setWinningLine(null);
  };

  // ✅ robust line style: explicitly map each pattern
  const getLineStyle = () => {
    if (!winningLine) return null;
    const [a, , c] = winningLine;

    const base = {
      position: 'absolute',
      background: '#000',
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
    };

    // rows
    if (winningLine.join(',') === '0,1,2') {
      return {
        ...base,
        width: '90%',
        height: '4px',
        left: '50%',
        top: '16.7%',
      };
    }
    if (winningLine.join(',') === '3,4,5') {
      return {
        ...base,
        width: '90%',
        height: '4px',
        left: '50%',
        top: '50%',
      };
    }
    if (winningLine.join(',') === '6,7,8') {
      return {
        ...base,
        width: '90%',
        height: '4px',
        left: '50%',
        top: '83.3%',
      };
    }

    // columns
    if (winningLine.join(',') === '0,3,6') {
      return {
        ...base,
        width: '4px',
        height: '90%',
        left: '16.7%',
        top: '50%',
      };
    }
    if (winningLine.join(',') === '1,4,7') {
      return {
        ...base,
        width: '4px',
        height: '90%',
        left: '50%',
        top: '50%',
      };
    }
    if (winningLine.join(',') === '2,5,8') {
      return {
        ...base,
        width: '4px',
        height: '90%',
        left: '83.3%',
        top: '50%',
      };
    }

    // diagonals
    if (winningLine.join(',') === '0,4,8') {
      return {
        ...base,
        width: '4px',
        height: '120%',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%) rotate(45deg)',
      };
    }
    if (winningLine.join(',') === '2,4,6') {
      return {
        ...base,
        width: '4px',
        height: '120%',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%) rotate(-45deg)',
      };
    }

    return base;
  };

  const result = calculateWinner(board);
  const winner = result?.winner;
  const status = winner
    ? `Winner: ${winner}`
    : board.every((square) => square !== null)
    ? 'Game Draw!'
    : `Next player: ${xIsNext ? 'X' : 'O'}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-black pt-24 pb-12 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md relative">
        <div className="p-6">
          <div className="text-2xl font-bold mb-4 text-center">{status}</div>
          <div className="grid grid-cols-3 gap-2 relative">
            {board.map((square, index) => (
              <button
                key={index}
                className={`w-24 h-24 text-4xl font-bold flex items-center justify-center rounded
                  ${
                    square === 'X'
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      : square === 'O'
                      ? 'bg-pink-100 text-pink-600 hover:bg-pink-200'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                onClick={() => handleClick(index)}
              >
                {square}
              </button>
            ))}
            {winningLine && <div style={getLineStyle()} />}
          </div>
          <button
            className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
            onClick={resetGame}
          >
            Reset Game
          </button>
        </div>
      </div>
    </div>
  );
};

export default XOGame;
